import React, { useMemo, useState } from "react";

import {
  Box,
  Paper,
  Stack,
  TextField,
  Autocomplete,
  Typography,
  Chip,
  Divider,
  Checkbox,
  Button,
  IconButton,
} from "@mui/material";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import { MaterialReactTable } from "material-react-table";

import { ComparisonTablePlaceholder } from "./TablePlaceholder";
import TreeViewDrawer from "../../../../../../../components/Drawers/TreeViewDrawer";
import useEmsProjectsTreeData from "../../../../../../../components/Drawers/TreeViewDrawer/useEmsProjectsTreeData";
import EmsProjectsTreeView from "../../../../../../../components/Drawers/TreeViewDrawer/EmsProjectsTreeView";
import { mockData } from "../mockData";

const auditMetricOptions = [
  "Quantity",
  "Dimension",
  "Cost",
  "Schedule",
  "Weight",
];

const metricColors = {
  Quantity: "#e8f5e9",
  Dimension: "#fff3e0",
  Cost: "#e3f2fd",
  Schedule: "#fce4ec",
  Weight: "#f3e5f5",
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const formatSelectionLabel = (sel) => {
  if (!sel) return "";
  const parts = [
    sel.plant?.name,
    sel.wbs?.name || sel.wbs?.projectName,
    sel.doc?.documentName || sel.doc?.uid || sel.dwgNumber || "",
  ].filter(Boolean);
  return parts.join(" / ") || "Untitled";
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function MSelectionBar() {
  const [leftSelection, setLeftSelection] = useState(null);
  const [rightSelection, setRightSelection] = useState(null);

  const [pickerSide, setPickerSide] = useState(null); // "left" | "right" | null

  const [auditMetrics, setAuditMetrics] = useState([]);

  const treeData = useEmsProjectsTreeData();

  // ─────────────────────────────────────────
  // Ready State
  // ─────────────────────────────────────────

  const comparisonReady =
    leftSelection && rightSelection && auditMetrics.length > 0;

  // ─────────────────────────────────────────
  // Dynamic Columns
  // ─────────────────────────────────────────

  const columns = useMemo(() => {
    const leftLabel = leftSelection
      ? formatSelectionLabel(leftSelection)
      : "Side A";

    const rightLabel = rightSelection
      ? formatSelectionLabel(rightSelection)
      : "Side B";

    const baseColumns = [
      {
        accessorKey: "semanticId",
        header: "UID",
        size: 180,
        enablePinning: true,
      },

      {
        accessorKey: "component",
        header: "Component",
        size: 250,
      },

      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        muiTableHeadCellProps: {
          align: "center",
        },

        muiTableBodyCellProps: {
          align: "center",
        },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          const statusConfig = {
            Matched: {
              color: "success",
              variant: "filled",
            },

            "Variance Found": {
              color: "warning",
              variant: "filled",
            },

            "Major Variance": {
              color: "error",
              variant: "filled",
            },

            "Critical Variance": {
              color: "error",
              variant: "filled",
            },

            "Missing In A": {
              color: "info",
              variant: "outlined",
            },

            "Missing In B": {
              color: "info",
              variant: "outlined",
            },

            Pending: {
              color: "default",
              variant: "outlined",
            },
          };

          const config = statusConfig[value] || {
            color: "default",
            variant: "outlined",
          };

          return (
            <Chip
              size="small"
              label={value}
              color={config.color}
              variant={config.variant}
              sx={{
                fontWeight: 700,
                minWidth: 120,
              }}
            />
          );
        },
      },
    ];

    const subColumnStyle = ({ metric }) => ({
      grow: false,
      size: 80,
      muiTableHeadCellProps: {
        align: "center",
        sx: {
          bgcolor: metricColors[metric],
        },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          bgcolor: metricColors[metric],
        },
      },
    });

    const metricColumns = auditMetrics.map((metric) => {
      return {
        id: metric,

        header: metric,

        muiTableHeadCellProps: {
          sx: {
            bgcolor: metricColors[metric],
            fontWeight: 800,
            textAlign: "center",
            borderLeft: "1px solid",
            borderColor: "divider",
          },
        },

        columns: [
          {
            accessorKey: `${metric}.left`,
            header: leftLabel,

            size: 140,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const value = row.original?.[metric]?.left;

              return (
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {value ?? "-"}
                </Box>
              );
            },
          },

          {
            accessorKey: `${metric}.right`,
            header: rightLabel,

            size: 140,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const value = row.original?.[metric]?.right;

              return (
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {value ?? "-"}
                </Box>
              );
            },
          },

          {
            accessorKey: `${metric}.delta`,
            header: "\u0394",

            size: 110,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const delta = row.original?.[metric]?.variance;

              if (delta === null || delta === undefined) {
                return "-";
              }

              if (typeof delta === "number") {
                return (
                  <Box
                    sx={{
                      fontWeight: 700,
                      color:
                        delta > 0
                          ? "success.main"
                          : delta < 0
                            ? "error.main"
                            : "text.secondary",
                    }}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </Box>
                );
              }

              return (
                <Chip
                  label={delta}
                  size="small"
                  color={
                    delta === "Matched"
                      ? "success"
                      : delta === "Mismatch" || delta === "Critical"
                        ? "error"
                        : "warning"
                  }
                  variant="outlined"
                />
              );
            },
          },
        ],
      };
    });

    return [...baseColumns, ...metricColumns];
  }, [auditMetrics, leftSelection, rightSelection]);

  // ─────────────────────────────────────────
  // Picker Callbacks
  // ─────────────────────────────────────────

  const handlePickerSelect = (target) => {
    if (pickerSide === "left") {
      setLeftSelection(target);
    } else if (pickerSide === "right") {
      setRightSelection(target);
    }
    setPickerSide(null);
  };

  // ─────────────────────────────────────────
  // Render Selection Side
  // ─────────────────────────────────────────

  const renderSelectionSide = (title, selection, side) => {
    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: selection ? "primary.light" : "divider",
          bgcolor: "background.default",
          transition: "border-color 0.2s ease",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CompareArrowsIcon color="primary" fontSize="small" />
            <Typography fontWeight={700}>{title}</Typography>
          </Stack>

          {selection ? (
            <Stack spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <DescriptionIcon color="primary" fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatSelectionLabel(selection)}
                </Typography>
                <IconButton
                  size="small"
                  title="Change selection"
                  onClick={() => setPickerSide(side)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AccountTreeIcon />}
              onClick={() => setPickerSide(side)}
              sx={{
                py: 2,
                borderStyle: "dashed",
                textTransform: "none",
              }}
            >
              Select Drawing
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* ───────────────────────────────────────── */}
      {/* Tree Picker Panel (Left Side) */}
      {/* ───────────────────────────────────────── */}

      <TreeViewDrawer open={Boolean(pickerSide)} anchor="left">
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {pickerSide === "left" ? "Select Side A Drawing" : "Select Side B Drawing"}
            </Typography>
            <IconButton size="small" onClick={() => setPickerSide(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <EmsProjectsTreeView
              data={treeData}
              activePlantId={undefined}
              onSelect={handlePickerSelect}
              pickerMode
            />
          </Box>
        </Box>
      </TreeViewDrawer>

      {/* ───────────────────────────────────────── */}
      {/* Main Content */}
      {/* ───────────────────────────────────────── */}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Selection Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={2.5}>
            {/* Two Comparison Sides */}
            <Stack
              direction={{
                xs: "column",
                xl: "row",
              }}
              spacing={2}
            >
              {renderSelectionSide("Comparison Side A", leftSelection, "left")}
              {renderSelectionSide("Comparison Side B", rightSelection, "right")}
            </Stack>

            {/* Metrics */}
            {comparisonReady && (
              <>
                <Divider />

                <Stack
                  direction={{
                    xs: "column",
                    lg: "row",
                  }}
                  spacing={2}
                  justifyContent="center"
                >
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={auditMetricOptions}
                    value={auditMetrics}
                    onChange={(e, value) => setAuditMetrics(value)}
                    sx={{ minWidth: 500 }}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} size="small" />
                        {option}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({
                            index,
                          })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Audit Metrics"
                        placeholder="Select metrics"
                      />
                    )}
                  />
                </Stack>
              </>
            )}
          </Stack>
        </Paper>

        {/* Table */}
        {!comparisonReady ? (
          <ComparisonTablePlaceholder />
        ) : (
          <MaterialReactTable
            columns={columns}
            data={mockData}
            enableColumnActions={false}
            enableSorting={false}
            enableStickyHeader
            enableDensityToggle={false}
            enableFullScreenToggle
            muiTableContainerProps={{
              sx: {
                maxHeight: "65vh",
              },
            }}
            initialState={{
              density: "compact",
              pagination: {
                pageSize: 10,
                pageIndex: 0,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}
